<template>
    <div>
        <div class="d-flex align-center flex-wrap px-3 pt-2">
            <v-text-field
                v-model="busca"
                dense
                outlined
                single-line
                hide-details
                clearable
                class="campo-busca"
                label="Buscar regra"
                append-icon="mdi-magnify"
            />

            <v-spacer/>

            <v-btn v-if="podeEditar" color="primary" small @click="$refs.dialog.abrir(null)">
                <v-icon left small>mdi-plus</v-icon>
                Nova regra
            </v-btn>
        </div>

        <v-card elevation="2" class="ma-2">
            <v-card-text class="pa-2">
                <v-data-table
                    :headers="headers"
                    :items="itens"
                    :search="busca"
                    :loading="loading"
                    :mobileBreakpoint="0"
                    :items-per-page="25"
                    :footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
                    dense
                >
                    <template v-slot:item.severidade="{ item }">
                        <v-chip x-small label dark :color="item.severidade === 'critico' ? 'red darken-1' : 'amber darken-2'">
                            {{ item.severidade === 'critico' ? 'Crítico' : 'Aviso' }}
                        </v-chip>
                    </template>

                    <template v-slot:item.condicao="{ item }">
                        <span class="font-weight-medium">{{ rotuloMetrica(item.metrica) }}</span>
                        {{ textoOperador(item.operador) }}
                        {{ formatarValor(item.metrica, item.limite) }}
                        <span class="text--secondary">por {{ item.duracao_minutos }} min</span>
                    </template>

                    <template v-slot:item.escopo="{ item }">
                        <span v-if="item.escopo === 'todos'" class="text-caption">
                            Todos os servidores
                        </span>
                        <span v-else class="text-caption">
                            <span v-if="item.escopo === 'exceto'" class="text--secondary">exceto </span>
                            {{ nomesDosHosts(item.hosts) }}
                        </span>
                    </template>

                    <template v-slot:item.destinatarios="{ item }">
                        <span v-if="item.destinatarios.length" class="text-caption">
                            {{ item.destinatarios.join(', ') }}
                        </span>
                        <span v-else class="text-caption orange--text text--darken-3">
                            <v-icon x-small color="orange darken-3">mdi-email-off-outline</v-icon>
                            sem e-mail
                        </span>
                    </template>

                    <template v-slot:item.ativo="{ item }">
                        <v-switch
                            :input-value="item.ativo"
                            :disabled="!podeEditar"
                            dense
                            inset
                            hide-details
                            class="mt-0 pt-0"
                            @change="definirAtivo(item, $event)"
                        />
                    </template>

                    <template v-slot:item.acoes="{ item }">
                        <v-btn icon small @click="$refs.dialog.abrir(item)">
                            <v-icon small>{{ podeEditar ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                        </v-btn>
                        <v-btn v-if="podeEditar" icon small @click="remover(item)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <RegraDialog ref="dialog" @salvar="salvar" @testar="testar"/>
    </div>
</template>

<script>
import RegraDialog from './RegraDialog';
import { carregarCatalogo, formatarValor, metrica } from './catalogo';
import { carregarServidores, nomeDoHost } from './servidores';

export default {
    name: 'TabRegras',

    components: {
        RegraDialog,
    },

    data: () => ({
        loading: false,
        busca: '',
        itens: [],
        headers: [
            { text: 'Severidade', value: 'severidade', width: 110 },
            { text: 'Regra', value: 'nome' },
            { text: 'Condição', value: 'condicao', sortable: false },
            { text: 'Servidores', value: 'escopo', width: 170, sortable: false },
            { text: 'Destinatários', value: 'destinatarios', sortable: false },
            { text: 'Ativa', value: 'ativo', width: 80, sortable: false },
            { text: '', value: 'acoes', width: 90, sortable: false, align: 'end' },
        ],
    }),

    mounted() {
        this.carregar();
    },

    computed: {
        // Sem esta permissão a aba vira consulta: as rotas de escrita recusam no
        // servidor, então esconder o botão evita prometer o que não vai acontecer.
        podeEditar() {
            return this.checkPermission('zapercontrol_monitoramento_regras') || this.isDiretor();
        },
    },

    methods: {
        formatarValor,

        rotuloMetrica(coluna) {
            return metrica(coluna).label;
        },

        nomesDosHosts(hosts) {
            return (hosts || []).map(nomeDoHost).join(', ') || '—';
        },

        textoOperador(operador) {
            return {
                '>': 'acima de',
                '>=': 'a partir de',
                '<': 'abaixo de',
                '<=': 'até',
            }[operador] || operador;
        },

        async carregar() {
            this.loading = true;
            try {
                await carregarCatalogo(this.$http);
                await carregarServidores(this.$http);

                const { data } = await this.$http.get('monitoramento/regras');

                this.itens = data;
            } catch (e) {
                this.notify('Não foi possível carregar as regras', 'error');
            }
            this.loading = false;
        },

        async salvar(regra) {
            this.loading = true;
            try {
                regra.id
                    ? await this.$http.put(`monitoramento/regras/${regra.id}`, regra)
                    : await this.$http.post('monitoramento/regras', regra);

                await this.carregar();
                this.notify('Regra salva');
            } catch (e) {
                this.loading = false;
            }
        },

        async definirAtivo(item, ativo) {
            try {
                await this.$http.put(`monitoramento/regras/${item.id}`, { ...item, ativo });
                await this.carregar();
                this.notify(ativo ? 'Regra ativada' : 'Regra desativada');
            } catch (e) {
                // Recarrega para o switch voltar a mostrar o que está gravado, e
                // não o que o clique tentou fazer.
                await this.carregar();
            }
        },

        async remover(item) {
            const ok = await this.$root.$confirm(
                'Excluir regra',
                `A regra "${item.nome}" deixa de existir, com todo o histórico de disparos dela, `
                + 'e nenhum e-mail será mais enviado por essa condição. Confirma?',
            );

            if (!ok) return;

            try {
                await this.$http.delete(`monitoramento/regras/${item.id}`);
                await this.carregar();
                this.notify('Regra excluída');
            } catch (e) {
                await this.carregar();
            }
        },

        async testar(regra) {
            try {
                const { data } = await this.$http.post(`monitoramento/regras/${regra.id}/testar`);
                this.notify(data.msg || 'E-mail de teste enviado');
            } catch (e) {
                // O interceptor do axios já mostrou o motivo.
            }
        },
    },
};
</script>

<style scoped>
.campo-busca {
    max-width: 320px;
}
</style>
